import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createBrowserRouter, createMemoryRouter, RouterProvider, Navigate, Outlet } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { AccessGateProvider, useAccessGate } from "./contexts/AccessGateContext";
import { CookieConsent } from "./components/CookieConsent";
import { ComingSoon } from "./components/ComingSoon";
import { ScrollToTop } from "./components/ScrollToTop";
import ShopifyEmbeddedBridge from "./components/ShopifyEmbeddedBridge";
import { useCartSync } from "./hooks/useCartSync";
import ProtectedRoute from "./components/ProtectedRoute";
import ShopifyAccountGuard from "./components/ShopifyAccountGuard";
import Index from "./pages/Index";
import MyGifts from "./pages/MyGifts";
import ProductDetail from "./pages/ProductDetail";
import UnifiedLogin from "./pages/UnifiedLogin";
import ResetPassword from "./pages/ResetPassword";
import MerchantDashboard from "./pages/MerchantDashboard";
import MerchantOnboarding from "./pages/MerchantOnboarding";
import MerchantWidgetGuide from "./pages/MerchantWidgetGuide";
import SplitPaymentStatus from "./pages/SplitPaymentStatus";
import AdminDashboard from "./pages/AdminDashboard";
import Pricing from "./pages/Pricing";
import AcceptInvite from "./pages/AcceptInvite";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import Contact from "./pages/Contact";
import RefundPolicy from "./pages/RefundPolicy";
import Demo from "./pages/Demo";
import NotFound from "./pages/NotFound";
import OAuthSuccess from "./pages/OAuthSuccess";
import GiftGallery from "./pages/GiftGallery";
import EventDiscussion from "./pages/EventDiscussion";
import PurchaseDiscussion from "./pages/PurchaseDiscussion";
import EventManagement from "./pages/EventManagement";
import JoinEvent from "./pages/JoinEvent";
import MyActivity from "./pages/MyActivity";
import FAQ from "./pages/FAQ";
import HowSplitPaymentsWork from "./pages/HowSplitPaymentsWork";
import ShakatahCheckout from "./pages/ShakatahCheckout";
import ShakatahSplitSetup from "./pages/ShakatahSplitSetup";
import ShopifyRedirect from "./pages/ShopifyRedirect";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import SplitCheckoutHowItWorks from "./pages/SplitCheckoutHowItWorks";
import WooCommercePlugin from "./pages/WooCommercePlugin";
import { getShopifyStorefrontContext, syncShopifyStorefrontLocation } from "@/lib/shopifyStorefront";

const queryClient = new QueryClient();

// NOTE: Using function declaration instead of const arrow to prevent TDZ errors in production builds
function AccessGatedLayout() {
  const { hasAccess, grantAccess } = useAccessGate();

  // Sync carts with Shopify when user returns from checkout
  useCartSync();

  // hasAccess is already computed synchronously in context (includes bypass routes, URL params)
  // No need to re-check here - just use the context value directly
  if (!hasAccess) {
    return (
      <>
        <ShopifyEmbeddedBridge />
        <ComingSoon onAccessGranted={grantAccess} />
      </>
    );
  }

  return (
    <>
      <ShopifyEmbeddedBridge />
      <ScrollToTop />
      <Outlet />
      <CookieConsent />
    </>
  );
}

// Standalone/Admin pages use browser history; the storefront block supplies its
// initial location separately because a srcdoc document has no URL query.
const storefrontContext = getShopifyStorefrontContext();
const isShopifyStorefrontProxy = typeof window !== 'undefined' && window.location.pathname.startsWith('/apps/splitkart/');
const routeDefinitions = [
  {
    element: <AccessGatedLayout />,
    children: [
      { path: "/", element: <Index /> },
      { path: "/my-purchases", element: <MyGifts /> },
      { path: "/my-activity", element: <MyActivity /> },
      { path: "/gallery", element: <GiftGallery /> },
      { path: "/pricing", element: <Pricing /> },
      { path: "/product/:handle", element: <ProductDetail /> },
      { path: "/shakatah-checkout", element: <ShakatahCheckout /> },
      { path: "/shakatah-split", element: <ShakatahSplitSetup /> },
      { path: "/shakatah-dashboard", element: <Navigate to="/merchant-dashboard" replace /> },
      { path: "/merchant", element: <Navigate to="/merchant-dashboard" replace /> },
      { path: "/merchant-login", element: <UnifiedLogin /> },
      { path: "/login", element: <UnifiedLogin /> },
      { path: "/reset-password", element: <ResetPassword /> },
      { path: "/customer-login", element: <Navigate to="/login?type=customer" replace /> },
      { path: "/merchant/widget-guide", element: <ProtectedRoute><ShopifyAccountGuard><MerchantWidgetGuide /></ShopifyAccountGuard></ProtectedRoute> },
      { 
        path: "/merchant/onboarding", 
        element: <ProtectedRoute><ShopifyAccountGuard><MerchantOnboarding /></ShopifyAccountGuard></ProtectedRoute>
      },
      { 
        path: "/merchant-onboarding", 
        element: <ProtectedRoute><ShopifyAccountGuard><MerchantOnboarding /></ShopifyAccountGuard></ProtectedRoute>
      },
      { 
        path: "/merchant-dashboard", 
        element: <ProtectedRoute><ShopifyAccountGuard><MerchantDashboard /></ShopifyAccountGuard></ProtectedRoute>
      },
      { 
        path: "/admin", 
        element: <ProtectedRoute><AdminDashboard /></ProtectedRoute>
      },
      { path: "/split/:requestId", element: <SplitPaymentStatus /> },
      { path: "/events/:reminderId", element: <EventDiscussion /> },
      { path: "/purchases/:id", element: <PurchaseDiscussion /> },
      { path: "/join/:type/:token", element: <JoinEvent /> },
      { path: "/event-management", element: <EventManagement /> },
      { path: "/accept-invite", element: <AcceptInvite /> },
      { path: "/privacy-policy", element: <PrivacyPolicy /> },
      { path: "/privacy", element: <Navigate to="/privacy-policy" replace /> },
      { path: "/terms-of-service", element: <TermsOfService /> },
      { path: "/terms", element: <Navigate to="/terms-of-service" replace /> },
      { path: "/contact", element: <Contact /> },
      { path: "/refund-policy", element: <RefundPolicy /> },
      { path: "/refund", element: <Navigate to="/refund-policy" replace /> },
      { path: "/faq", element: <FAQ /> },
      { path: "/blog", element: <Blog /> },
      { path: "/blog/:slug", element: <BlogPost /> },
      { path: "/woocommerce", element: <WooCommercePlugin /> },
      { path: "/stories", element: <Navigate to="/" replace /> },
      
      { path: "/how-it-works", element: <HowSplitPaymentsWork /> },
      { path: "/how-split-payments-work", element: <HowSplitPaymentsWork /> },
      { path: "/split-checkout-how-it-works", element: <SplitCheckoutHowItWorks /> },
      { path: "/demo", element: <Demo /> },
      { path: "/oauth-success", element: <OAuthSuccess /> },
      { path: "/shopify-redirect", element: <ShopifyRedirect /> },
      { path: "*", element: <NotFound /> },
    ],
  },
];
const router = storefrontContext
  ? createMemoryRouter(routeDefinitions, { initialEntries: [`${storefrontContext.pathname}${storefrontContext.search}`] })
  : createBrowserRouter(routeDefinitions, { basename: isShopifyStorefrontProxy ? '/apps/splitkart' : '/' });

if (storefrontContext) {
  router.subscribe((state) => syncShopifyStorefrontLocation(state.location));
}

// NOTE: Using function declaration instead of const arrow to prevent TDZ errors in production builds
function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <AccessGateProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <RouterProvider router={router} />
          </TooltipProvider>
        </AccessGateProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
